import React, { useState } from 'react';
import { X, Tag, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { membershipApi } from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutModal = ({ 
  isOpen, 
  onClose, 
  membership, 
  userId,
  onCheckout 
}) => {
  const [couponCode, setCouponCode] = useState('');
  const [couponStatus, setCouponStatus] = useState(null); // null, 'validating', 'valid', 'invalid'
  const [couponMessage, setCouponMessage] = useState('');
  const [discountedPrice, setDiscountedPrice] = useState(null);
  const [originalPrice, setOriginalPrice] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  if (!isOpen || !membership) return null;

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;

    setCouponStatus('validating');
    setCouponMessage('');

    try {
      const response = await fetch(`${API_URL}/api/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code: couponCode.trim(),
          product_id: membership.id,
          product_type: 'membership',
          user_id: userId
        })
      });

      const data = await response.json();

      if (data.valid) {
        setCouponStatus('valid');
        setCouponMessage(data.message);
        setOriginalPrice(data.original_price);
        setDiscountedPrice(data.discounted_price);
      } else {
        setCouponStatus('invalid');
        setCouponMessage(data.message || 'Invalid coupon code');
        setDiscountedPrice(null);
        setOriginalPrice(null);
      }
    } catch (error) {
      setCouponStatus('invalid');
      setCouponMessage('Failed to validate coupon');
      setDiscountedPrice(null);
    }
  };

  const handleRemoveCoupon = () => {
    setCouponCode('');
    setCouponStatus(null);
    setCouponMessage('');
    setDiscountedPrice(null);
    setOriginalPrice(null);
  };

  const handleProceedToCheckout = async () => {
    setIsProcessing(true);
    try {
      // Pass the coupon code to the checkout function
      await onCheckout(membership, couponStatus === 'valid' ? couponCode : null);
    } catch (error) {
      console.error('Checkout error:', error);
    }
    setIsProcessing(false);
  };

  const displayPrice = discountedPrice !== null ? discountedPrice : membership.price;
  const showDiscount = couponStatus === 'valid' && discountedPrice !== null;

  return (
    <div className="checkout-modal-overlay" onClick={onClose}>
      <div className="checkout-modal" onClick={e => e.stopPropagation()}>
        <button className="checkout-modal-close" onClick={onClose}>
          <X size={20} />
        </button>

        <div className="checkout-modal-header">
          <h2>Complete Your Purchase</h2>
        </div>

        <div className="checkout-modal-content">
          {/* Plan Summary */}
          <div className="checkout-plan-summary">
            <h3 className="checkout-plan-name">{membership.name}</h3>
            <div className="checkout-plan-price">
              {showDiscount && (
                <span className="checkout-original-price">${originalPrice}</span>
              )}
              <span className="checkout-current-price">
                ${displayPrice}
                <span className="checkout-price-period">
                  {membership.period === 'month' ? '/mo' : membership.period === 'year' ? '/year' : ''}
                </span>
              </span>
            </div>
          </div>

          {/* Features */}
          {membership.features && membership.features.length > 0 && (
            <div className="checkout-features">
              <p className="checkout-features-title">What's included:</p>
              <ul className="checkout-features-list">
                {membership.features.map((feature, idx) => (
                  <li key={idx}>
                    <CheckCircle size={14} />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Coupon Code Section */}
          <div className="checkout-coupon-section">
            <label className="checkout-coupon-label">
              <Tag size={16} />
              Have a coupon code?
            </label>
            
            {couponStatus !== 'valid' ? (
              <div className="checkout-coupon-input-group">
                <input
                  type="text"
                  placeholder="Enter coupon code"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  className="checkout-coupon-input"
                  disabled={couponStatus === 'validating'}
                />
                <button 
                  className="checkout-coupon-apply-btn"
                  onClick={handleApplyCoupon}
                  disabled={!couponCode.trim() || couponStatus === 'validating'}
                >
                  {couponStatus === 'validating' ? (
                    <Loader2 size={16} className="spin" />
                  ) : (
                    'Apply'
                  )}
                </button>
              </div>
            ) : (
              <div className="checkout-coupon-applied">
                <span className="checkout-coupon-badge">
                  <CheckCircle size={14} />
                  {couponCode}
                </span>
                <button 
                  className="checkout-coupon-remove-btn"
                  onClick={handleRemoveCoupon}
                >
                  Remove
                </button>
              </div>
            )}

            {couponMessage && (
              <p className={`checkout-coupon-message ${couponStatus === 'valid' ? 'success' : 'error'}`}>
                {couponStatus === 'valid' ? (
                  <CheckCircle size={14} />
                ) : (
                  <AlertCircle size={14} />
                )}
                {couponMessage}
              </p>
            )}
          </div>

          {/* Total */}
          <div className="checkout-total">
            <span className="checkout-total-label">Total</span>
            <span className="checkout-total-amount">
              ${displayPrice}
              {membership.period === 'month' ? '/mo' : membership.period === 'year' ? '/year' : ''}
            </span>
          </div>
        </div>

        <div className="checkout-modal-footer">
          <button 
            className="checkout-proceed-btn"
            onClick={handleProceedToCheckout}
            disabled={isProcessing}
          >
            {isProcessing ? (
              <>
                <Loader2 size={18} className="spin" />
                Processing...
              </>
            ) : (
              'Proceed to Checkout'
            )}
          </button>
          <p className="checkout-secure-note">
            🔒 Secure checkout powered by Stripe
          </p>
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;
